import * as R from 'ramda';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Container from '../../common/components/Container/Container';
import Loading from '../../common/components/Loading/Loading';
import TitleContainer from '../../common/components/TitleContainer/TitleContainer';
import { HardcodedConfigResolver } from '../../config';
import { Routes } from '../../config/routes';
import { FlowConfigContext } from '../../providers/FlowConfigContext';
import withRouteAnalytics from '../../services/analytics/withRouteAnalytics';
import { getNetworkByChainId } from '../../utils/utils';
import { networkConfigs } from '../../common/types';
import { Screens } from '../../services/analytics/screens';
type DescriptionKeys =
  | 'solanaDescription'
  | 'solanaFirstDelayDescription'
  | 'solanaSecondDelayDescription'
  | 'evmDescription'
  | 'evmFirstDelayDescription'
  | 'evmSecondDelayDescription';

const capitalizeFirstLetter = (str: string) => str.charAt(0).toUpperCase() + str.slice(1);

export function AwaitingTransactionConfirmation(): JSX.Element {
  const { t } = useTranslation();
  const { initialized, chainNetwork, chainName, signedTx } = useContext(FlowConfigContext);
  const [chainDisplay, setChainDisplay] = useState<string>(t('chains.unknown'));
  const timer: { current: NodeJS.Timeout | undefined } = useRef(undefined);
  const [delayDescription, setDelayDescription] = useState<string | null>(null);

  const clearTimer = () => {
    if (timer.current) {
      clearInterval(timer.current);
    }
    timer.current = undefined;
  };

  const setUpdateDescriptionTimer = (key: DescriptionKeys, delayMilliseconds: number): void => {
    clearTimer();
    timer.current = setInterval(() => {
      setDelayDescription(`screens.awaitingTransactionConfirmation.${key}`);
    }, delayMilliseconds);
  };

  useEffect(() => {
    // Clear interval on component unmount
    return function cleanup() {
      clearTimer();
    };
  }, []);

  useEffect(() => {
    if (!initialized) {
      return;
    }

    const routeConfig =
      HardcodedConfigResolver.loadConfig(chainName).routeConfigs[Routes.AWAITING_TRANSACTION_CONFIRMATION];

    if (!delayDescription && chainName !== 'solana') {
      setUpdateDescriptionTimer('evmDescription', routeConfig.firstMessageUpdateDelayMilliseconds);
      return;
    }

    if (delayDescription && R.endsWith('evmDescription', delayDescription) && chainName !== 'solana') {
      setUpdateDescriptionTimer('evmFirstDelayDescription', routeConfig.thirdMessageUpdateDelayMilliseconds);
      return;
    }

    if (delayDescription && R.endsWith('evmFirstDelayDescription', delayDescription) && chainName !== 'solana') {
      setUpdateDescriptionTimer('evmSecondDelayDescription', routeConfig.thirdMessageUpdateDelayMilliseconds);
      return;
    }

    if (!delayDescription && chainName === 'solana') {
      setUpdateDescriptionTimer('solanaDescription', routeConfig.firstMessageUpdateDelayMilliseconds);
      return;
    }

    if (delayDescription && R.endsWith('solanaDescription', delayDescription)) {
      setUpdateDescriptionTimer('solanaFirstDelayDescription', routeConfig.secondMessageUpdateDelayMilliseconds);
      return;
    }

    if (delayDescription && R.endsWith('solanaFirstDelayDescription', delayDescription)) {
      setUpdateDescriptionTimer('solanaSecondDelayDescription', routeConfig.thirdMessageUpdateDelayMilliseconds);
      return;
    }

    clearTimer();
  }, [initialized, delayDescription, chainName]);

  useEffect(() => {
    if (!initialized) {
      return;
    }
    // Precendence: the chain network (if a string), then chain, then unknown
    const useChainParam = parseInt(chainNetwork) ? chainName : chainNetwork || chainName;
    // Fall back to displaying the unknown chain translation value if no translation is available for given chain variable
    let chain = t([`chains.${useChainParam}`, capitalizeFirstLetter(useChainParam)]);

    // for Ethereum chain networks, the chainNetwork value can be an integer id, so we need to resolve the details
    // from chainId
    if (chainName === 'ethereum' && chainNetwork) {
      const networkConfig = getNetworkByChainId(parseInt(chainNetwork), networkConfigs);
      if (networkConfig) {
        chain = networkConfig.network;
      }
    }

    setChainDisplay(chain);
  }, [initialized, chainNetwork, chainName]);

  return (
    <Container>
      <header
        data-testid="AWAITING_TRANSACTION_CONFIRMATION_SCREEN"
        className="flex-grow-0 flex-shrink-0 flex justify-center items-center mb-4"
      ></header>

      <TitleContainer center>
        <h1 className="mb-4" data-testid="screens.awaitingTransactionConfirmation.title">
          {t('screens.awaitingTransactionConfirmation.title', { chain: chainDisplay })}
        </h1>
        <div>
          {!R.isNil(delayDescription) && (
            <>
              <div className="leading-6 text-textParagraph" data-testid={delayDescription}>
                {t(delayDescription, { chain: chainDisplay })}
              </div>
            </>
          )}
        </div>
      </TitleContainer>
      <div className="mb-8">
        <Loading />
      </div>
      <></>
      <small
        className="text-xs text-center overflow-wrap text-textNeutral"
        data-testid="AWAITING_TRANSACTION_SIGNED_TX"
      >
        {t('screens.awaitingTransactionConfirmation.transactionId')} {signedTx}
      </small>
    </Container>
  );
}
export default withRouteAnalytics(AwaitingTransactionConfirmation, Screens.AWAITING_TXN_CONFIRMATION);
